import React from 'react';
import { useTranslation } from 'react-i18next';
import { TwTheme } from '../../../Tw';
import { cn } from '../../common/utils';

const eventGroupsColor = TwTheme.extend.colors.eventGroups;

export type MetricGroup = {
  title: string;
  metrics: string[];
  color?: string;
};

export type StackedLineMetricConfig = {
  key: string;
  title: string;
  icon?: string;
  metrics: string[];
  metricGroups: MetricGroup[];
};

export type StackedLineChartProps = {
  config: StackedLineMetricConfig;
  data: Record<string, number>;
  totalKey?: string; // The key to use for total calculation, defaults to 'duration'
  className?: string;
  height?: string; // e.g., 'h-3', 'h-4', etc.
};

// Default color mapping for common metric types
const getMetricGroupColor = (groupTitle: string, metrics: string[]): string => {
  const titleLower = groupTitle.toLowerCase();
  const metricsStr = metrics.join('').toLowerCase();
  
  if (titleLower.includes('travelling') || metricsStr.includes('travelling')) {
    if (metricsStr.includes('start') || metricsStr.includes('end')) {
      return eventGroupsColor.travellingEndpoint;
    }
    if (metricsStr.includes('vertical')) {
      return eventGroupsColor.travellingVertical;
    }
    return eventGroupsColor.travellingHorizontal;
  }
  
  if (titleLower.includes('handling') || metricsStr.includes('handling')) {
    return eventGroupsColor.handling;
  }
  
  if (titleLower.includes('picking') || metricsStr.includes('picking')) {
    return eventGroupsColor.picking;
  }
  
  if (titleLower.includes('reorder') || metricsStr.includes('reorder')) {
    return eventGroupsColor.reordering;
  }
  
  if (titleLower.includes('idle') || metricsStr.includes('idle')) {
    return eventGroupsColor.idle;
  }
  
  // Default color for unknown groups
  return '#6B7280';
};

const StackedLineChart: React.FC<StackedLineChartProps> = ({
  config,
  data,
  totalKey = 'duration',
  className,
  height = 'h-3',
}) => {
  const { t } = useTranslation('app');
  
  const total = data[totalKey] || data.duration || 0;
  
  if (total <= 1) return null;

  // Calculate segments based on metric groups
  const segments = config.metricGroups.map(group => {
    const groupTotal = group.metrics.reduce((sum, metric) => {
      return sum + (data[metric] || 0);
    }, 0);
    
    const percentage = ((groupTotal / total) * 100) % 100;
    const color = group.color || getMetricGroupColor(group.title, group.metrics);
    
    return {
      title: group.title,
      percentage,
      color,
      value: groupTotal,
      metrics: group.metrics,
    };
  }).filter(segment => segment.percentage > 0);

  const hasAnySegments = segments.length > 0;

  return (
    <div
      data-component="StackedLineChart"
      className={cn(
        'flex flex-row rounded-sm w-full',
        hasAnySegments ? height : '',
        className,
      )}
      title={`${config.title}: ${segments.map(s => `${s.title} (${s.percentage.toFixed(1)}%)`).join(', ')}`}
    >
      {segments.map((segment, index) => (
        <div
          key={index}
          title={`${segment.title}: ${segment.value}s (${segment.percentage.toFixed(1)}%)`}
          style={{
            width: `${segment.percentage}%`,
            backgroundColor: segment.color,
          }}
          className="transition-all duration-200 hover:opacity-80"
        />
      ))}
    </div>
  );
};

export default StackedLineChart; 